import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Button, useStyleConfig} from '@chakra-ui/react'
import {TextTitle} from '../../text-title'
import {TextTitleDescription} from '../../text-title-description'
import {remCalc} from '../../../../theme/_utils'
import {Link} from 'react-router-dom'
import {getAlignmentStyles, calculateVerticalPadding} from '../../utils'
import {useGetCategoriesData} from '../../../../custom-api/hooks/use-get-categories-data'
import {safeBuildUrl, getGlobalLink} from '../../../../utils/contentstack-utils'
import HeroBannerCountDown from '../../hero-banner-countdown'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import SanitizedHtml from '../../../html-sanitizer'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {useSeoH1} from '../../../../pages/home'

export const HeroBannerText = ({
    maxWidth = remCalc(1920),
    textBlock,
    variant,
    hasPaddings,
    countdown,
    buttonText,
    isSmallHeight = false,
    isMediumHeight = false,
    isFirstElement,
}) => {
    const styles = useStyleConfig('HeroBannerText')
    const {buildUrl, locale} = useMultiSite()

    const stylesBasedOnAlignment = getAlignmentStyles(textBlock?.desktopAlignment, styles)
    const desktopTextAlignment = textBlock?.desktopAlignment?.includes('center') ? 'center' : 'left'
    const verticalPadding = calculateVerticalPadding('', hasPaddings)

    const categoriesData = useGetCategoriesData()
    const {data: productsData} = useProducts(
        {
            parameters: {
                ids:
                    buttonText?.productsfcc?.data?.length > 0
                        ? buttonText.productsfcc.data[0].id
                        : '',
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'hero-banner-index',
            },
        },
        {
            enabled: buttonText?.productsfcc?.data?.length > 0,
        },
    )
    const {url, label, isAbsolute} = getGlobalLink({
        link: buttonText,
        categoriesData,
        productsData,
        locale: locale?.alias || locale?.id?.toLowerCase(),
    })

    const buttonProps = isAbsolute
        ? {
              as: 'a',
              href: url,
              target: '_blank',
              rel: 'noopener noreferrer',
          }
        : {
              as: Link,
              to: safeBuildUrl(url, buildUrl),
          }

    const countdownEndDate = countdown?.[0]?.end_date

    const inlinePaddingEnabled =
        textBlock?.desktopAlignment !== 'middle-right' &&
        textBlock?.desktopAlignment !== 'middle-left'
    const normalPadding = {base: remCalc(16), lg: remCalc(40)}
    const customPadding = {base: 'unset', sm: 'unset', md: 'unset', lg: remCalc(16), '2xl': 'unset'}

    const {hasSeoH1} = useSeoH1()

    return (
        <Flex
            maxW={maxWidth}
            {...stylesBasedOnAlignment.container}
            data-testid={variant}
            px={inlinePaddingEnabled ? {...normalPadding} : {...customPadding}}
            sx={{
                '@media screen and (min-width: 1399px) and (max-width: 1439px)': {
                    px: 'unset',
                },
            }}
            zIndex={2}
        >
            <Flex
                textAlign={{base: 'center', md: 'center', lg: desktopTextAlignment}}
                {...stylesBasedOnAlignment.textContent}
                py={verticalPadding}
                data-testid="hero-banner-text-flex"
            >
                {textBlock?.label && (
                    <TextTitle
                        alignment={{base: 'center', md: 'center', lg: desktopTextAlignment}}
                        data-testid="hero-banner-text-label"
                        mb={
                            isSmallHeight
                                ? remCalc(16)
                                : {
                                      base: remCalc(16),
                                      sm: remCalc(16),
                                      md: remCalc(24),
                                      lg: remCalc(24),
                                  }
                        }
                        color={variant === 'black' ? 'text.inverse' : 'text.secondary'}
                        {...styles.textTitle}
                    >
                        <SanitizedHtml as={Box} html={textBlock?.label} />
                    </TextTitle>
                )}
                <TextTitleDescription
                    alignment={{base: 'center', md: 'center', lg: desktopTextAlignment}}
                    title={
                        <Box>
                            <SanitizedHtml
                                role="heading"
                                as={!hasSeoH1 && isFirstElement ? 'h1' : 'h2'}
                                html={textBlock?.title}
                            />
                        </Box>
                    }
                    titleSpacing={
                        isSmallHeight
                            ? remCalc(16)
                            : isMediumHeight
                            ? remCalc(24)
                            : {base: remCalc(16), md: remCalc(24), lg: remCalc(40)}
                    }
                    color={variant === 'black' ? 'text.inverse' : 'text.default'}
                    childrenTextColor={variant === 'black' ? 'text.inverse' : 'text.default'}
                    mb={
                        isSmallHeight
                            ? remCalc(16)
                            : isMediumHeight
                            ? remCalc(24)
                            : {base: remCalc(16), md: remCalc(24), lg: remCalc(40)}
                    }
                    noMinHeight
                    {...styles.textTitleDescription}
                >
                    <SanitizedHtml as={Box} html={textBlock?.bodyCopy} />
                </TextTitleDescription>
                {countdownEndDate && (
                    <Flex
                        justify={{
                            base: 'center',
                            lg: textBlock?.desktopAlignment?.includes('center')
                                ? 'center'
                                : 'flex-start',
                        }}
                    >
                        <HeroBannerCountDown endingDate={countdownEndDate} variant={variant} />
                    </Flex>
                )}
                {label && url && (
                    <Box textAlign={{base: 'center', md: 'center', lg: desktopTextAlignment}}>
                        <Button
                            {...buttonProps}
                            variant={variant === 'black' ? 'inverse-primary' : 'primary'}
                            {...styles.HeroBannerButton}
                        >
                            {label}
                        </Button>
                    </Box>
                )}
            </Flex>
        </Flex>
    )
}

HeroBannerText.propTypes = {
    textBlock: PropTypes.shape({
        label: PropTypes.string,
        title: PropTypes.string,
        bodyCopy: PropTypes.string,
        desktopAlignment: PropTypes.string,
        tabletMobileAlignment: PropTypes.string,
    }),
    buttonText: PropTypes.object,
    maxWidth: PropTypes.string,
    variant: PropTypes.string,
    hasPaddings: PropTypes.bool,
    countdown: PropTypes.array,
    isSmallHeight: PropTypes.bool,
    isMediumHeight: PropTypes.bool,
    isFirstElement: PropTypes.bool,
}
